import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'aakeeo:region';
const EVENT = 'aakeeo:region-change';
const USD_TO_INR = 83;

function readTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    return '';
  }
}

function readLang() {
  if (typeof navigator === 'undefined') return '';
  return (navigator.language || '').toLowerCase();
}

export function detectRegion() {
  if (typeof window === 'undefined') return 'US';
  const tz = readTimeZone();
  if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') return 'IN';
  const lang = readLang();
  if (lang.startsWith('en-in') || lang.startsWith('hi')) return 'IN';
  return 'US';
}

export function getStoredRegion() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'IN' || stored === 'US') return stored;
  } catch (_) {}
  return null;
}

export function resolveRegion() {
  return getStoredRegion() || detectRegion();
}

export function setRegion(r) {
  if (r !== 'IN' && r !== 'US') return;
  try { localStorage.setItem(STORAGE_KEY, r); } catch (_) {}
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: r }));
  }
}

// Subscribe is called by React once per hook caller; return value is the
// unsubscribe fn. Events fire when the user toggles ₹/$ or when the global
// setRegion is called programmatically.
function subscribe(callback) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

// Client snapshot: the real, detected/stored region.
const getClientSnapshot = () => resolveRegion();

// Server snapshot: always 'US'. React guarantees this is used during hydration,
// eliminating the mismatch between prerender (UTC → 'US') and Asia/Kolkata clients.
const getServerSnapshot = () => 'US';

// useSyncExternalStore is React 18's supported primitive for "external state
// that may differ between server and client". It guarantees hydration uses
// the server snapshot, then switches to the client snapshot after commit — no
// layout/text diff between SSR HTML and first client render.
export function useCurrency() {
  const region = useSyncExternalStore(subscribe, getClientSnapshot, getServerSnapshot);
  const price = ({ usd, inr }) => (region === 'IN' ? inr : usd);
  return { region, setRegion, price };
}

// Rough INR conversion for narrative copy (case-study stats, market sizing).
// Rounds to nice bands: ₹k, ₹L, ₹Cr.
export function convertNarrative(usd) {
  const inr = usd * USD_TO_INR;
  if (inr < 1000) return `₹${Math.round(inr / 100) * 100}`;
  if (inr < 100000) return `₹${Math.round(inr / 1000)}k`;
  if (inr < 10000000) {
    const lakhs = inr / 100000;
    const v = lakhs >= 10 ? Math.round(lakhs) : lakhs.toFixed(1).replace(/\.0$/, '');
    return `₹${v}L`;
  }
  const crores = inr / 10000000;
  const v = crores >= 10 ? Math.round(crores) : crores.toFixed(1).replace(/\.0$/, '');
  return `₹${v}Cr`;
}
